import {
  Box,
  Flex,
  Grid,
  GridItem,
  Heading,
  Spinner,
  Text,
  VStack,
} from '@chakra-ui/react';
import { AgentComplianceReviewByShareLinkTokenQuery } from '@client/graphql/__generated__/types';
import { FC, useCallback, useEffect, useState } from 'react';
import { createSearchParams, useNavigate } from 'react-router-dom';
import {
  DocumentActionButtons,
  DocumentList,
  DocumentSuggestionsList,
  DocumentViewer,
  FolderSuggestionsCard,
  KeyTermsPanel,
  PropertySummaryCard,
  SuccessfulChecksList,
} from './components';
import {
  ComplianceSuccessfulCheck,
  ComplianceSuggestion,
  Participants,
} from './components/types';
import { useAgentComplianceReviewDocument } from './hooks/useAgentComplianceReviewDocument';
import { useAgentComplianceReviewKeyTerms } from './hooks/useAgentComplianceReviewKeyTerms';
import { useAgentDocumentValidation } from './hooks/useAgentDocumentValidation';

type AgentComplianceReviewOverview =
  AgentComplianceReviewByShareLinkTokenQuery['agentComplianceReviewByShareLinkToken'];

interface AgentComplianceReviewDesktopProps {
  overview: AgentComplianceReviewOverview;
  /** Currently selected document (from the `?documentId=` query param). */
  selectedDocumentId: string | undefined;
  /** Share-link token (`?token=` query param), preserved across navigation. */
  token: string | undefined;
  /** Folder UUID (route param) the review belongs to. */
  ingestionFolderUuid: string | undefined;
}

interface RightRailProps {
  suggestions: ComplianceSuggestion[];
  successfulChecks: ComplianceSuccessfulCheck[];
  hasSelection: boolean;
  loading: boolean;
  /** True until the per-document detail has loaded for the selection. */
  hasDetail: boolean;
  /** Called with a row's field ids when a suggestion/check row is clicked. */
  onSelectFields: (fieldIds: string[]) => void;
}

/**
 * Right rail: document-level suggestions + passing checks for the selected
 * document (derived client-side from the raw detail). Shows a spinner while the
 * detail loads and a muted placeholder when nothing is selected (or the
 * selected document has no findings).
 *
 * Clicking a suggestion/check row calls `onSelectFields(row.fieldIds)`, which
 * the page lifts into `activeFieldId` to scroll/center the PDF on the field.
 */
const RightRail: FC<RightRailProps> = ({
  suggestions,
  successfulChecks,
  hasSelection,
  loading,
  hasDetail,
  onSelectFields,
}) => {
  if (!hasSelection) {
    return (
      <Text color="compliance.text.muted" fontSize="sm">
        Select a document to see its suggestions and checks.
      </Text>
    );
  }

  const hasFindings = suggestions.length > 0 || successfulChecks.length > 0;

  return (
    <VStack align="stretch" spacing={5}>
      {/* Document-level actions sit above the suggestions (per Figma). */}
      <DocumentActionButtons />

      {loading && !hasDetail ? (
        <Flex justify="center" py={8}>
          <Spinner color="compliance.loading.text" />
        </Flex>
      ) : hasFindings ? (
        <>
          <DocumentSuggestionsList
            suggestions={suggestions}
            onSelectFields={onSelectFields}
          />
          <SuccessfulChecksList
            checks={successfulChecks}
            onSelectFields={onSelectFields}
          />
        </>
      ) : (
        <Text color="compliance.text.muted" fontSize="sm">
          No suggestions or checks for this document.
        </Text>
      )}
    </VStack>
  );
};

/**
 * Desktop three-pane layout for the agent compliance review page.
 *
 * - **Left rail**: property summary, the key-terms panel, the folder-level
 *   suggestions card, and the selectable document list.
 * - **Center**: the {@link DocumentViewer} for the selected document (its own
 *   "select a document" empty state shows when nothing is selected).
 * - **Right rail**: document-level suggestions and passing checks.
 *
 * Selection is URL-driven: clicking a document navigates to
 * `compliance-review/:ingestionFolderUuid?token=…&documentId=…`, preserving the
 * `?token=` query param, and the per-document detail query fires off the
 * `documentId` query param. The mobile single-column layout and the responsive
 * breakpoint switch are handled separately (next task).
 */
export const AgentComplianceReviewDesktop: FC<
  AgentComplianceReviewDesktopProps
> = ({ overview, selectedDocumentId, token, ingestionFolderUuid }) => {
  const navigate = useNavigate();

  const { document, loading, error } = useAgentComplianceReviewDocument({
    token,
    ingestionFolderUuid,
    ingestionDocumentId: selectedDocumentId,
  });

  const { keyTerms } = useAgentComplianceReviewKeyTerms();

  // The field id the user clicked a suggestion/check row to focus. Lifted here
  // so the right-rail lists and the center DocumentViewer stay in sync: the
  // viewer scrolls/centers the PDF on it and gives it the active outline.
  const [activeFieldId, setActiveFieldId] = useState<string | undefined>(
    undefined
  );

  // Clear the focused field when the selected document changes — a field id
  // from the previous document is meaningless against the new one.
  useEffect(() => {
    setActiveFieldId(undefined);
  }, [selectedDocumentId]);

  const onSelectFields = useCallback((fieldIds: string[]) => {
    setActiveFieldId(fieldIds[0]);
  }, []);

  const onSelectDocument = useCallback(
    (ingestionDocumentId: string) => {
      const search = createSearchParams({
        ...(token != null ? { token } : {}),
        documentId: ingestionDocumentId,
      }).toString();
      navigate(`/compliance-review/${ingestionFolderUuid}?${search}`);
    },
    [navigate, token, ingestionFolderUuid]
  );

  // Derive suggestions / successful checks / field highlights on the client
  // from the raw document detail — reusing the same shared compliance utils as
  // the drawer/extension, so the transformation isn't forked onto the backend.
  const validation = useAgentDocumentValidation(document);

  const hasSelection = selectedDocumentId != null;

  return (
    <Grid
      bg="compliance.background.primary"
      gap={0}
      h="100dvh"
      maxH="100dvh"
      // Single full-height row that can shrink below its content (minmax 0)
      // so each column scrolls independently instead of growing the page.
      overflow="hidden"
      templateColumns={{ base: '280px 1fr', xl: '320px 1fr 340px' }}
      templateRows="minmax(0, 1fr)"
      w="full"
    >
      {/* Left rail */}
      <GridItem
        bg="compliance.sidebar.bg"
        borderRightColor="compliance.sidebar.border"
        borderRightWidth={1}
        h="full"
        minH={0}
        overflowY="auto"
      >
        <VStack align="stretch" p={4} spacing={5}>
          <Box>
            <Text
              color="compliance.text.muted"
              fontSize="xs"
              fontWeight="medium"
              letterSpacing="wide"
              textTransform="uppercase"
            >
              Compliance review
            </Text>
            <Heading
              color="compliance.text.primary"
              fontSize="lg"
              mt={1}
              size="headline"
            >
              {overview.folderName}
            </Heading>
          </Box>

          <PropertySummaryCard
            folderName={overview.folderName}
            property={overview.property}
          />

          <KeyTermsPanel
            keyTerms={keyTerms}
            participants={
              overview.participants as Participants | null | undefined
            }
          />

          <FolderSuggestionsCard suggestions={overview.folderSuggestions} />

          <DocumentList
            documents={overview.documents}
            selectedDocumentId={selectedDocumentId}
            onSelectDocument={onSelectDocument}
          />
        </VStack>
      </GridItem>

      {/* Center pane: PDF viewer */}
      <GridItem h="full" minH={0} minWidth={0} overflow="hidden">
        {error ? (
          <Flex
            align="center"
            bg="compliance.pdf.bg"
            h="full"
            justify="center"
            p={6}
            w="full"
          >
            <Text color="compliance.notification.error.text" fontSize="sm">
              We couldn&apos;t load this document. Please try again.
            </Text>
          </Flex>
        ) : (
          <DocumentViewer
            activeFieldId={activeFieldId}
            documentName={document?.documentName}
            loading={loading}
            pdfUrl={document?.pdfUrl}
            submittedAt={
              document?.submittedAt != null
                ? String(document.submittedAt)
                : undefined
            }
            successFieldIds={validation.successFieldIds}
            validationFieldsMap={validation.validationFieldsMap}
            warningFieldIds={validation.errorFieldIds}
            warningFieldMessages={validation.fieldMessages}
          />
        )}
      </GridItem>

      {/* Right rail: suggestions + checks for the selected document */}
      <GridItem
        bg="compliance.sidebar.bg"
        borderLeftColor="compliance.sidebar.border"
        borderLeftWidth={1}
        display={{ base: 'none', xl: 'block' }}
        h="full"
        minH={0}
        overflowY="auto"
      >
        <Box p={4}>
          {error ? (
            <Text color="compliance.notification.error.text" fontSize="sm">
              We couldn&apos;t load this document.
            </Text>
          ) : (
            <RightRail
              hasDetail={document != null}
              hasSelection={hasSelection}
              loading={loading}
              successfulChecks={validation.successfulChecks}
              suggestions={validation.suggestions}
              onSelectFields={onSelectFields}
            />
          )}
        </Box>
      </GridItem>
    </Grid>
  );
};
